import React, { useState, useEffect } from 'react'
import * as XLSX from 'xlsx-js-style'
import { Download, AlertCircle } from 'lucide-react'

interface Highlight {
  id: string
  text: string
  startIndex: number
  length: number
  page: number
}

interface ExcelViewerProps {
  fileUrl: string
  fileName?: string
  highlights?: Highlight[]
  onHighlightClick?: (highlight: Highlight) => void
  className?: string
}

interface SheetData {
  name: string
  data: any[][]
  columnWidths: number[]
}

export const ExcelViewer: React.FC<ExcelViewerProps> = ({
  fileUrl,
  fileName = 'spreadsheet',
  highlights = [],
  onHighlightClick,
  className = ''
}) => {
  const [sheets, setSheets] = useState<SheetData[]>([])
  const [activeSheetIndex, setActiveSheetIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isOverlayVisible, setIsOverlayVisible] = useState(true)

  useEffect(() => {
    if (!fileUrl) {
      setLoading(false)
      return
    }

    loadExcelFile()
  }, [fileUrl])

  const loadExcelFile = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch the file as array buffer
      const response = await fetch(fileUrl)
      if (!response.ok) {
        throw new Error('Failed to load Excel file')
      }

      const arrayBuffer = await response.arrayBuffer()

      // Parse the Excel file
      const workbook = XLSX.read(arrayBuffer, { type: 'array' })

      // Extract all sheets
      const sheetsData: SheetData[] = workbook.SheetNames.map(sheetName => {
        const worksheet = workbook.Sheets[sheetName]

        // Convert to 2D array with styling
        const data = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: '',
          raw: false
        }) as any[][]

        // Get column widths (for better display)
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1')
        const columnWidths = []
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_col(col) + '1'
          const cell = worksheet[cellAddress]
          columnWidths.push(cell?.w?.length || 100)
        }

        return {
          name: sheetName,
          data,
          columnWidths
        }
      })

      setSheets(sheetsData)
      setLoading(false)
    } catch (err) {
      console.error('Error loading Excel file:', err)
      setError(err instanceof Error ? err.message : 'Failed to load Excel file')
      setLoading(false)
    }
  }

  const handleDownload = () => {
    const link = document.createElement('a')
    link.href = fileUrl
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Check if a cell contains highlighted text
  const isCellHighlighted = (cellValue: string): boolean => {
    if (!cellValue || highlights.length === 0) return false
    const cellText = String(cellValue).toLowerCase()
    return highlights.some(h =>
      cellText.includes(h.text.toLowerCase()) ||
      h.text.toLowerCase().includes(cellText)
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading spreadsheet...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center p-6 max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Failed to Load Spreadsheet
          </h3>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <button
            onClick={handleDownload}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Download File Instead
          </button>
        </div>
      </div>
    )
  }

  if (!fileUrl || sheets.length === 0) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center">
          <p className="text-gray-500">No spreadsheet available</p>
        </div>
      </div>
    )
  }

  const activeSheet = sheets[activeSheetIndex]

  return (
    <div className={`relative h-full flex flex-col bg-white ${className}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">
            {fileName}
          </span>
          {sheets.length > 1 && (
            <span className="text-xs text-gray-500">
              ({sheets.length} sheets)
            </span>
          )}
        </div>
        <button
          onClick={handleDownload}
          className="p-2 hover:bg-gray-100 rounded transition-colors flex items-center gap-2"
          title="Download"
        >
          <Download className="w-4 h-4" />
          <span className="text-sm">Download</span>
        </button>
      </div>

      {/* Sheet Tabs */}
      {sheets.length > 1 && (
        <div className="flex items-center gap-1 px-4 py-2 bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {sheets.map((sheet, index) => (
            <button
              key={index}
              onClick={() => setActiveSheetIndex(index)}
              className={`px-3 py-1.5 text-sm rounded transition-colors whitespace-nowrap ${
                activeSheetIndex === index
                  ? 'bg-white text-blue-600 font-medium border border-gray-200'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {sheet.name}
            </button>
          ))}
        </div>
      )}

      {/* Table Container */}
      <div className="flex-1 overflow-auto">
        <table className="min-w-full border-collapse">
          <tbody>
            {activeSheet.data.map((row, rowIndex) => (
              <tr key={rowIndex} className={rowIndex === 0 ? 'bg-gray-50' : ''}>
                {row.map((cell, cellIndex) => {
                  const isHighlighted = isCellHighlighted(cell)
                  const isHeader = rowIndex === 0

                  return (
                    <td
                      key={cellIndex}
                      className={`
                        border border-gray-200 px-3 py-2 text-sm
                        ${isHeader ? 'font-semibold text-gray-800' : 'text-gray-700'}
                        ${isHighlighted ? 'bg-yellow-100 border-yellow-400' : ''}
                      `}
                      style={{
                        minWidth: '100px',
                        maxWidth: '300px'
                      }}
                    >
                      {cell}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Highlight info overlay */}
      {highlights.length > 0 && isOverlayVisible && (
        <div className="absolute top-20 right-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 max-w-xs shadow-lg z-10">
          {/* Close button */}
          <button
            onClick={() => setIsOverlayVisible(false)}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close overlay"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="flex items-center gap-2 mb-2 pr-6">
            <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
            <div className="text-sm font-semibold text-gray-800">
              Evidence Located
            </div>
          </div>
          <div className="text-xs text-gray-600 mb-1">
            Found in this spreadsheet
          </div>
          <div className="mt-2 text-xs text-gray-700 bg-white p-2 rounded border">
            <div className="font-medium mb-1">Evidence:</div>
            <div className="italic">
              "{highlights[0].text.substring(0, 200)}{highlights[0].text.length > 200 ? '...' : ''}"
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-400">
            Highlighted cells contain matching text
          </div>
        </div>
      )}
    </div>
  )
}

export default ExcelViewer
